import React, { ComponentProps, ComponentType, lazy, Suspense } from "react";

import { Loader } from "./loader";

/**
 * A utility component that simplifies React.lazy + Suspense boilerplate.
 * Supports default exports and named exports (auto-resolved if unique).
 */
export const Lazy = <T extends ComponentType<any>>(
  component: () => Promise<Record<string, unknown>>,
  componentName?: string
) => {
  const LazyComponent = lazy(async () => {
    // eslint-disable-next-line no-useless-catch
    try {
      const x = await component();

      if (x.default) {
        return x as { default: T };
      }

      const keys = Object.keys(x).filter((k) => k !== "__esModule");

      if (keys.length === 1) {
        return {
          default: x[keys[0]] as T,
        };
      }

      if (componentName) {
        if (componentName in x) {
          return {
            default: x[componentName] as T,
          };
        }

        throw new Error(
          `Lazy import can't resolve named export specified in the componentName: ${componentName}`
        );
      }

      throw new Error(
        "Lazy import does not have a default export and has multiple named exports. You can specify the component to resolve through the second argument (componentName)."
      );
    } catch (e) {
      // Re-throw to let React.lazy handle the error
      throw e;
    }
  });

  const LazyWrapper = (props: ComponentProps<T>) => (
    <Suspense fallback={<Loader />}>
      <LazyComponent {...props} />
    </Suspense>
  );

  LazyWrapper.displayName = `Lazy(${componentName || "Component"})`;

  return LazyWrapper;
};
